import type { ImageLoader, PixiModule } from '../animation/animationController';
import { extractEvents } from '../av/events';
import { sessionTiming } from '../av/sessionController';
import type { Project } from '../domain/schemas';
import { SessionFrameRenderer } from '../video/frameRenderer';
import { takeAnimationSnapshot, takeSnapshot, type AnimationSnapshot, type ExportSnapshot } from '../video/snapshot';
import type { AudioFeatures } from '../visualizer/analysis';
import { captureReviewFrames, reviewSize, type CaptureOptions, type CapturedFrame, type ReviewLongEdge } from './capture';
import { buildReviewDocument, sha256Hex, type ReviewSourceInfo, type VisualReview } from './document';
import { gridOf, type FrameGrid } from './timestamps';

/**
 * Visual review export (Phase 5C.1): sources, snapshot, capture and document in one place,
 * shared by the Assistant workspace and the tests. Everything runs in the browser on a
 * frozen copy of the project; nothing is sent anywhere.
 */

export type ReviewSourceChoice = { kind: 'sync-session'; sessionId: string } | { kind: 'animation'; animationId: string };

export interface ReviewSourceOption {
  key: string;
  /** For the owner's own screen only (never written to review.json unless names are included). */
  label: string;
  choice: ReviewSourceChoice;
  grid: FrameGrid;
}

/** What a scene can be reviewed from: its sync sessions, then its animations. */
export function reviewSources(project: Project, sceneId: string): ReviewSourceOption[] {
  const out: ReviewSourceOption[] = [];
  for (const s of project.avSessions.filter((x) => x.sceneId === sceneId)) {
    const timing = sessionTiming(project, s);
    if (timing.durationSec > 0) out.push({ key: `s:${s.id}`, label: `Sync session · ${s.name}`, choice: { kind: 'sync-session', sessionId: s.id }, grid: gridOf(timing) });
  }
  for (const a of project.animations.filter((x) => x.sceneId === sceneId)) {
    out.push({ key: `a:${a.id}`, label: `Animation · ${a.name}`, choice: { kind: 'animation', animationId: a.id }, grid: gridOf({ durationSec: a.durationFrames / a.fps, animation: { fps: a.fps, durationFrames: a.durationFrames, loop: a.loop } }) });
  }
  return out;
}

export interface PreparedReview {
  snapshot: ExportSnapshot | AnimationSnapshot;
  grid: FrameGrid;
  source: ReviewSourceInfo;
  loop: string;
}

/** Freezes the project for one review (throws `SnapshotError` with a readable message). */
export function prepareReview(project: Project, choice: ReviewSourceChoice): PreparedReview {
  if (choice.kind === 'sync-session') {
    const snapshot = takeSnapshot(project, choice.sessionId);
    return { snapshot, grid: gridOf(snapshot.timing), source: { kind: 'sync-session', session: snapshot.session, animation: snapshot.animation }, loop: snapshot.timing.loop ? 'session-loop' : 'none' };
  }
  const snapshot = takeAnimationSnapshot(project, choice.animationId);
  return { snapshot, grid: gridOf(snapshot.timing), source: { kind: 'animation', animation: snapshot.animation }, loop: snapshot.animation.loop };
}

export interface ReviewExportRequest {
  prepared: PreparedReview;
  timestamps: readonly number[];
  selection: 'even' | 'custom';
  longEdge: ReviewLongEdge;
  brief?: { goal: string; constraints: string } | null;
  includeNames?: boolean;
  loadImage?: ImageLoader;
  /** Measured audio features of the session (null: not analysed yet; visualizers fall back to events, as in video export). */
  features?: AudioFeatures | null;
  signal?: AbortSignal;
  onProgress?: CaptureOptions['onProgress'];
  /** Test seams. */
  loadPixi?: () => Promise<PixiModule>;
  encode?: CaptureOptions['encode'];
  createdAt?: Date;
}

export interface ReviewExportResult {
  document: VisualReview;
  frames: CapturedFrame[];
  createMs: number;
  totalBytes: number;
}

export async function exportReview(req: ReviewExportRequest): Promise<ReviewExportResult> {
  const { prepared } = req;
  const { snapshot } = prepared;
  const { width, height } = reviewSize(snapshot.scene.width, snapshot.scene.height, req.longEdge);
  const session = prepared.source.kind === 'sync-session' ? prepared.source.session : null;
  const features = session ? (req.features ?? null) : null;
  const capture = await captureReviewFrames(() => SessionFrameRenderer.create(snapshot, width, height, { loadImage: req.loadImage, features, loadPixi: req.loadPixi }), req.timestamps, {
    width,
    height,
    signal: req.signal,
    onProgress: req.onProgress,
    encode: req.encode,
  });
  if (req.signal?.aborted) throw new DOMException('The review export was cancelled.', 'AbortError');
  const digests = await Promise.all(capture.frames.map((f) => sha256Hex(f.blob)));
  const document = buildReviewDocument({
    project: snapshot.project,
    scene: snapshot.scene,
    source: prepared.source,
    grid: prepared.grid,
    loop: prepared.loop,
    selection: req.selection,
    capture,
    digests,
    brief: req.brief ?? null,
    includeNames: req.includeNames,
    events: session ? ('events' in snapshot ? snapshot.events : extractEvents(snapshot.project, session)) : null,
    features,
    createdAt: req.createdAt,
  });
  return { document, frames: capture.frames, createMs: capture.createMs, totalBytes: capture.totalBytes };
}
